<template>
  <v-container>
    <v-layout
      wrap
      align-center
      justify-center
    >
      <v-container
        row
        justify-center
      >
        <!-- Personal Info Card -->
        <v-flex
          flex
          xs12
          sm12
          md12
          lg3
        >
          <v-card>
            <v-card-title class="pb-0 h6">
              <h2>{{ $t('components.overview.details') }}</h2>
            </v-card-title>
            <v-card-title class="text-body-2 py-1 font-weight-thin">
              <span>{{ $t('components.overview.security') }}</span>
            </v-card-title>
            <v-divider />
            <v-list
              dense
              class="text-body-2"
              aria-label="User Details"
            >
              <v-list-item>
                <v-list-item-content>
                  {{ $t('components.overview.profile.username') }}
                </v-list-item-content>
                <v-list-item-content class="align-end text-wrap">
                  {{ username }}
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>{{ $t('components.overview.profile.email') }}</v-list-item-content>
                <v-list-item-content class="align-end text-wrap">
                  {{ email }}
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>{{ $t('components.overview.profile.points') }}</v-list-item-content>
                <v-list-item-content class="align-end text-wrap">
                  {{ zushinPoints }}
                </v-list-item-content>
              </v-list-item>
              <v-btn
                aria-label="Download your data"
                block
                small
                color="accent"
                class="font-weight-thin"
                @click="downloadUserData"
              >
                {{ $t('components.overview.buttons.downloadData') }}
                <v-icon
                  small
                  right
                >
                  fad fa-download
                </v-icon>
              </v-btn>
            </v-list>
          </v-card>

          <!-- Donations Card -->
          <v-card v-if="showTotalDonations">
            <v-card-title class="pb-0">
              <h3>{{ $t('components.overview.donations.title') }}</h3>
            </v-card-title>
            <v-card-title class="text-body-2 pt-1 pb-0 font-weight-thin">
              <span>{{ $t('components.overview.donations.subtitle') }}</span>
            </v-card-title>
            <v-divider />
            <v-list
              dense
              aria-label="Donations"
            >
              <v-list-item>
                <v-list-item-content>{{ $t('components.overview.donations.remaining') }}</v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ currentTotalDonations }} / {{ totalRequiredDonations }}
                </v-list-item-content>
              </v-list-item>
              <v-progress-linear
                background-color="primary"
                color="success"
                role="progressbar"
                :value="donationsPercentage"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="donationsPercentage"
                aria-label="Donation Progress"
              />
              <v-btn
                block
                small
                color="accent"
                class="font-weight-thin mt-4"
                :to="toBuy"
                :aria-label="$t('components.overview.buttons.buyZP')"
              >
                {{ $t('components.overview.buttons.buyZP') }}
                <v-icon
                  small
                  right
                >
                  fad fa-hands-heart
                </v-icon>
              </v-btn>
              <v-btn
                aria-label="Donate"
                block
                small
                color="accent"
                class="font-weight-thin mt-4"
                target="_blank"
                :href="toDonate"
              >
                {{ $t('components.overview.buttons.donate') }}
                <v-icon
                  small
                  right
                >
                  fad fa-hands-heart
                </v-icon>
              </v-btn>
            </v-list>
          </v-card>
        </v-flex>

        <!-- Referral System and Contact Info -->
        <v-flex
          offset-xs0
          xs12
          offset-sm0
          sm12
          offset-md0
          md12
          offset-lg1
          lg8
        >
          <v-card>
            <v-app-bar
              flat
              color="primary"
            >
              <v-toolbar-title>{{ $t('components.overview.contact.title') }}</v-toolbar-title>
            </v-app-bar>
            <v-card-actions class="row xs12 sm12 md12 lg12 justify-center align-center">
              <v-btn
                aria-label="Facebook Page"
                text
                color="accent"
                @click="viewReportCard(links.fbPage)"
              >
                {{ $t('components.overview.contact.buttons.fbPage') }}
              </v-btn>
              <v-btn
                aria-label="Discord Invite"
                text
                color="accent"
                @click="viewReportCard(links.discord)"
              >
                {{ $t('components.overview.contact.buttons.discord') }}
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card>
            <v-app-bar
              flat
              color="primary"
            >
              <v-toolbar-title>{{ $t('components.overview.referral.title') }}</v-toolbar-title>
            </v-app-bar>
            <v-card-text color="secondary">
              <v-stepper
                v-if="$vuetify.breakpoint.mdAndUp"
                alt-labels
              >
                <v-stepper-header>
                  <v-stepper-step
                    step="1"
                    :complete-icon="$t('components.overview.referral.guide.0.icon')"
                    complete
                    color="transparent"
                    :aria-describedby="`step-desc-1`"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span
                          style="text-center"
                          v-on="on"
                          v-text="$t('components.overview.referral.guide.0.title')"
                        />
                      </template>
                      <span v-text="$t('components.overview.referral.guide.0.description')" />
                    </v-tooltip>
                  </v-stepper-step>

                  <v-divider />

                  <v-stepper-step
                    step="2"
                    :complete-icon="$t('components.overview.referral.guide.1.icon')"
                    complete
                    color="transparent"
                    :aria-describedby="`step-desc-2`"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span
                          style="text-center"
                          v-on="on"
                          v-text="$t('components.overview.referral.guide.1.title')"
                        />
                      </template>
                      <span v-text="$t('components.overview.referral.guide.1.description')" />
                    </v-tooltip>
                  </v-stepper-step>

                  <v-divider />


                  <v-stepper-step
                    step="3"
                    :complete-icon="$t('components.overview.referral.guide.2.icon')"
                    complete
                    color="transparent"
                    :aria-describedby="`step-desc-3`"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span
                          style="text-center"
                          v-on="on"
                          v-text="$t('components.overview.referral.guide.2.title')"
                        />
                      </template>
                      <span v-text="$t('components.overview.referral.guide.2.description')" />
                    </v-tooltip>
                  </v-stepper-step>

                  <v-divider />

                  <v-stepper-step
                    step="4"
                    :complete-icon="$t('components.overview.referral.guide.3.icon')"
                    complete
                    color="transparent"
                    :aria-describedby="`step-desc-4`"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span
                          style="text-center"
                          v-on="on"
                          v-text="$t('components.overview.referral.guide.3.title')"
                        />
                      </template>
                      <span v-text="$t('components.overview.referral.guide.3.description')" />
                    </v-tooltip>
                  </v-stepper-step>

                  <v-divider />

                  <v-stepper-step
                    step="5"
                    :complete-icon="$t('components.overview.referral.guide.4.icon')"
                    complete
                    color="transparent"
                    :aria-describedby="`step-desc-5`"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span
                          style="text-center"
                          v-on="on"
                          v-text="$t('components.overview.referral.guide.4.title')"
                        />
                      </template>
                      <span v-text="$t('components.overview.referral.guide.4.description')" />
                    </v-tooltip>
                  </v-stepper-step>
                </v-stepper-header>
              </v-stepper>

              <v-list
                v-if="$vuetify.breakpoint.smAndDown"
                three-line
              >
                <v-list-item
                  v-for="(currentStep, index) in referralGuides"
                  :key="`step-${index}`"
                >
                  <v-list-item-action size="25">
                    <v-icon>{{ currentStep.icon }}</v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title v-text="currentStep.title" />
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>

            <v-card-text>
              <v-flex lg12>
                <v-alert
                  type="info"
                >
                  {{ $t('components.overview.referral.guideLink') }}
                </v-alert>
                <v-alert
                  v-show="hasCopied"
                  type="success"
                  role="status"
                  aria-live="polite"
                >
                  {{ $t('components.overview.referral.copied') }}
                </v-alert>
                <v-text-field
                  class="input-group--focused"
                  prepend-icon="fad fa-share-all"
                  aria-label="Referral Link"
                  :value="referralLink"
                  single-line
                  flat
                  readonly
                  @focus="onReferralLink"
                />
                <v-flex
                  align-center
                  text-center
                  justify-center
                  lg12
                >
                  <span
                    class="fb-share-button"
                    :data-href="referralLink"
                    data-layout="button"
                    data-size="small"
                  >
                    <v-btn
                      aria-label="Share Referral Link to FB"
                      class="fb-xfbml-parse-ignore"
                      :href="`https://www.facebook.com/sharer/sharer.php?u=${referralLink}&src=sdkpreparse`"
                      ripple
                      icon
                      text
                      color="accent"
                      target="_blank"
                    >
                      <v-icon
                        small
                        aria-hidden="true"
                      >fab fa-facebook</v-icon>
                    </v-btn>
                  </span>
                  <v-btn
                    aria-label="Share Referral Link to Twitter"
                    class="twitter-share-button"
                    :href="`https://twitter.com/intent/tweet?url=${referralLink}`"
                    data-size="large"
                    ripple
                    icon
                    text
                    color="accent"
                    target="_blank"
                  >
                    <v-icon
                      small
                      aria-hidden="true"
                    >
                      fab fa-twitter
                    </v-icon>
                  </v-btn>
                </v-flex>
              </v-flex>
            </v-card-text>

            <v-card-text class="text-center">
              <v-subheader
                text-center
                inset
              >
                {{ $t('components.overview.referral.totalReferred') }}
              </v-subheader>
              <v-divider inset />
              <v-progress-circular
                class="mt-3"
                :size="100"
                :width="15"
                :rotate="90"
                :value="totalReferred"
                color="accent"
                :aria-label="$t('components.overview.referral.totalReferred')"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="totalReferred"
              >
                {{ totalReferred }}
              </v-progress-circular>
            </v-card-text>
            <v-card-text class="text-center">
              <v-subheader
                text-center
                inset
              >
                {{ $t('components.overview.referral.totalReferredCompleted') }}
              </v-subheader>
              <v-divider inset />
              <v-progress-circular
                class="mt-3"
                :size="100"
                :width="15"
                :rotate="90"
                :value="totalReferredCompleted"
                color="success"
                :aria-label="$t('components.overview.referral.totalReferredCompleted')"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="totalReferredCompleted"
              >
                {{ totalReferredCompleted }}
              </v-progress-circular>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-container>
    </v-layout>
  </v-container>
</template>


<script>
import { mapGetters } from 'vuex';
import links from "@/constants/links.js";
import config from "@/constants/config.js";
import { toPrice, toPercentageInverse } from "@/utils/math.utils"
import { toCurrencySymbol } from "@/utils/money.utils";

export default {
  name: "Overview",
  props: {
    username: {
        type: String,
        default: "",
    },
    email: {
        type: String,
        default: "",
    },
    zushinPoints: {
        type: Number,
        default: 0,
    },
    totalReferred: {
        type: Number,
        default: 0,
    },
    totalReferredCompleted: {
        type: Number,
        default: 0,
    },
    referralLink: {
        type: String,
        default: "",
    },
  },
  data() {
    return {
        links: links.communities,
        hasCopied: false,
        totalDonations: 0
    }
  },
  computed: {
    ...mapGetters(['paymentCurrency']),
    referralGuides() {
        return [
            {
                title: this.$t("components.overview.referral.guide.0.title"),
                icon: this.$t("components.overview.referral.guide.0.icon"),
                description: this.$t("components.overview.referral.guide.0.description"),
            },
            {
                title: this.$t("components.overview.referral.guide.1.title"),
                icon: this.$t("components.overview.referral.guide.1.icon"),
                description: this.$t("components.overview.referral.guide.1.description"),
            },
            {
                title: this.$t("components.overview.referral.guide.2.title"),
                icon: this.$t("components.overview.referral.guide.2.icon"),
                description: this.$t("components.overview.referral.guide.2.description"),
            },
            {
                title: this.$t("components.overview.referral.guide.3.title"),
                icon: this.$t("components.overview.referral.guide.3.icon"),
                description: this.$t("components.overview.referral.guide.3.description"),
            },
            {
                title: this.$t("components.overview.referral.guide.4.title"),
                icon: this.$t("components.overview.referral.guide.4.icon"),
                description: this.$t("components.overview.referral.guide.4.description"),
            }
        ]
    },
    toBuy() {
        return links.routes.checkout.replace('/:id', '').replace('/:provider', '');
    },
    toDonate() {
        return "https://www.paypal.com/donate/?hosted_button_id=DNE9M6HBGPSKY";
    },
    showTotalDonations() {
        return config.features.showTotalDonations && !this.hasReachedDonations && Math.trunc(this.totalDonations) > 0;
    },
    hasReachedDonations() {
        return Math.trunc(this.totalDonations) > config.totalRequiredDonations;
    },
    totalRequiredDonations() {
        return `${toCurrencySymbol(this.paymentCurrency)}${toPrice(parseInt(config.totalRequiredDonations)).split('.')[0]}`;
    },
    currentTotalDonations() {
        return `${toCurrencySymbol(this.paymentCurrency)}${toPrice(Math.trunc(this.totalDonations)).split('.')[0]}`;
    },
    donationsPercentage() {
        return toPercentageInverse(Math.trunc(this.totalDonations), config.totalRequiredDonations)
    }
  },
  beforeMount() {
      this.$api.getTotalDonations()
        .then(({ total }) => {
            this.totalDonations = total;
        });
  },
  methods: {
    /**
     * Copy link on referral click
     */
    onReferralLink($event) {
      if (!this.hasCopied) {
        $event.target.select();
        this.hasCopied = document.execCommand("copy");
        setTimeout(() => {
          this.hasCopied = false;
          $event.target.selection = 0;
        }, 5000);
      }
    },
    /**
     * Will handle the view game card to external url
     */
    viewReportCard(link) {
      if (link) {
        // track event
        // this.$ga.event("profile-report-card", "link", link);
        // open
        window.open(link);
      }
    },
    /**
     * Will handle download of data
     */
    downloadUserData() {
      if (links.endpoints.userDataDownload) {
        window.open(links.endpoints.userDataDownload);
      }
    },
    /**
     * On click of donations send us the ga event
     */
    clickedDonations() {
        // track event
        // this.$ga.event("profile", "donations", this.showTotalDonations);
    },
  }
};
</script>
